import axios from "axios";
import { normalizeText } from "@/utils/index";

export enum ConceptType {
  Context = "0",
  Disease = "1",
  Number = "2",
  LabProcedure = "4",
  Anatomy = "5",
  Drug = "6",
  Procedure = "7",
  Unit = "9",
  Measurement = "11",
  Dosage = "13",
  LabResult = "14",
  Food = "15",
  People = "17",
  Race = "18",
  Symptom = "19",
  Date = "23",
  Microbe = "25",
  Finding = "26",
  Device = "28",
  Cell = "29",
  BioChem = "30",
  Name = "33",
  DiagProcedure = "35"
}

/**
 * const getTokens4 = () => {
	const selection = window.getSelection();

	const tokens = [selection.anchorNode.parentElement.closest(".token")];

	while (!tokens[tokens.length - 1].contains(selection.focusNode)) tokens.push(tokens[tokens.length - 1].nextSibling);

	return tokens.slice(0, -1);
};
 *
 */

namespace AnalysisEndpoint {
  export interface Token {
    tok: string;
    cpos: [number, number];
  }

  export interface Block {
    bcharpos: number;
  }

  export interface Response {
    tokens: Token[];
    meta: {
      blocks: Block[];
      computetime: number;
    };
    content: Record<ConceptType, ConceptMatch[]>;
  }
}

export interface Text {
  phrase: string;
  charpos: [number, number];
  tokpos: [number, number];
  context?: Record<string, string>;
}

export interface Concept {
  conceptid: string;
  conceptname: string;
  concepttype: ConceptType;
  text?: Text;
  texts?: Text[];
}

export interface ConceptMatch {
  concept: Concept;
  context: Record<string, string>;
  refers: Record<string, ConceptMatch>;
  relationships?: Record<string, ConceptMatch[]>;
  labels?: string[];
  score: number;
}

export interface Token {
  index: number;
  text: string;
  span: [number, number];
  delimiter: string;
}

export interface AnalysisResults {
  time: number;
  text: string;
  tokens: Readonly<Readonly<Token>[]> | Token[];
  blocks: Readonly<Readonly<Readonly<Token>[]>[]> | Token[][];
  matches:
    | Readonly<Record<ConceptType, Readonly<Readonly<ConceptMatch>[]>>>
    | Record<ConceptType, ConceptMatch[]>;
}

const processRawTokens = (
  text: string,
  tokens: AnalysisEndpoint.Token[]
): Readonly<Readonly<Token>[]> =>
  Object.freeze(
    tokens.map((token, index, arr) => {
      const isNotLast = index < arr.length - 1;
      const next = isNotLast ? arr[index + 1].cpos[0] : -1;

      return Object.freeze({
        index,
        text: token.tok.replace(/\\n/g, "\n"),
        span: token.cpos,
        delimiter: next != -1 ? text.substring(token.cpos[1], next) : ""
      });
    })
  );

const processBlocks = (
  tokens: Readonly<Readonly<Token>[]>,
  blocks: AnalysisEndpoint.Block[]
): Readonly<Readonly<Readonly<Token>[]>[]> => {
  const result: Token[][] = [[]];
  let currentBlockIndex = 1;

  for (let token of tokens) {
    if (
      currentBlockIndex === blocks.length ||
      token.span[1] < blocks[currentBlockIndex].bcharpos
    ) {
      result[result.length - 1].push(token);
      continue;
    }

    currentBlockIndex++;
    result.push([token]);
  }

  return Object.freeze(result.map(block => Object.freeze(block)));
};

const deepFreeze = (object: any) => {
  for (const name of Object.getOwnPropertyNames(object)) {
    const value = object[name];

    if (value && typeof value === "object") {
      deepFreeze(value);
    }
  }

  return Object.freeze(object);
};

export const analyseText = (
  text: string,
  endpoint: string,
  headers: any
): Promise<AnalysisResults> => {
  const normalized = normalizeText(text);

  return axios
    .post<AnalysisEndpoint.Response>(endpoint, normalized, {
      headers
    })
    .then(response => response.data)
    .then(data => {
      const tokens = processRawTokens(normalized, data.tokens);

      const blocks = processBlocks(tokens, data.meta.blocks);

      return Object.freeze({
        time: data.meta.computetime,
        text: normalized,
        matches: deepFreeze(data.content),
        tokens,
        blocks
      });
    });
};
